import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert, MenuItem } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ExportMenu } from '../../components/ExportMenu';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ProjectFormDialog } from './ProjectFormDialog';
import { projectsApi, type Project, type CreateProjectInput, type ProjectStatus } from '../../api/projects.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const STATUS_COLOR: Record<ProjectStatus, 'default' | 'info' | 'success' | 'warning' | 'error'> = {
  planned: 'default', active: 'info', on_hold: 'warning', completed: 'success', closed: 'default', cancelled: 'error',
};

export function ProjectListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [projectType, setProjectType] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [deleting, setDeleting] = useState<Project | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['projects', page, limit, search, status, projectType],
    queryFn: () => projectsApi.list({ page, limit, search: search || undefined, status: status || undefined, projectType: projectType || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['projects'] });

  const createMut = useMutation({
    mutationFn: (input: CreateProjectInput) => projectsApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateProjectInput }) => {
      const { clientId: _clientId, ...rest } = input;
      return projectsApi.update(id, rest);
    },
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => projectsApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Project>[] = [
    { key: 'projectCode', label: 'Code', width: 110 },
    { key: 'projectName', label: 'Name' },
    { key: 'startDate', label: 'Start' },
    { key: 'endDate', label: 'End' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={STATUS_COLOR[r.status]} /> },
  ];

  return (
    <>
      <PageHeader
        title="Project Master"
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="projects" permission="project.export" filters={{ search, status, projectType }} />
            {hasPermission('project.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Project</Button>}
          </Stack>
        }
      />
      <Stack direction="row" spacing={2} mb={2}>
        <TextField size="small" placeholder="Search name / code" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        <TextField size="small" select label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 160 }}>
          <MenuItem value="">All</MenuItem>
          {Object.keys(STATUS_COLOR).map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
        </TextField>
        <TextField size="small" select label="Type" value={projectType} onChange={(e) => { setProjectType(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {['installation', 'maintenance', 'supply', 'turnkey', 'survey', 'other'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
        </TextField>
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('project.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('project.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <ProjectFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete project"
        message={`Soft-delete ${deleting?.projectName}? Blocked if it has sites.`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
